import {create, type TSetter} from "@/store/createStore";
import type {
  TNonFunctionKeys,
  TPackageCategory,
  TPackagePaymentPeriodType,
  TSubscriptionWithDiscountOptions,
} from "@/store/types";
import useUserStore from "@/store/user";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TState = {
  tiktok: TSubscriptionWithDiscountOptions[];
  instagram: TSubscriptionWithDiscountOptions[];
  both: TSubscriptionWithDiscountOptions[];
  isInstagramSelected: boolean;
  isTiktokSelected: boolean;
  selectedPeriod: TPackagePaymentPeriodType;
  selectedPlan: TSubscriptionWithDiscountOptions | null;
  isUpgradePlanModalOpen: boolean;
  isPaymentInProgress: boolean;
  setter: TSetter<TState>;
  hasEssentialsPlan(): boolean;
  contactSales(subject: string, companyName: string): void;
  getCost(
    formatter: (value: number) => string,
    plan: TSubscriptionWithDiscountOptions,
    period?: TPackagePaymentPeriodType,
  ): string;
  getIsCurrentPlan(plan: TSubscriptionWithDiscountOptions): Boolean;
  getPlanCategory(): TPackageCategory;
  getPlans(category?: TPackageCategory): TSubscriptionWithDiscountOptions[];
};

const YEAR_IN_MONTHS = 12;

const initialState = {
  tiktok: [],
  instagram: [],
  both: [],
  isInstagramSelected: true,
  isTiktokSelected: false,
  selectedPeriod: "YEARLY",
  selectedPlan: null,
  isUpgradePlanModalOpen: false,
  isPaymentInProgress: false,
} satisfies Pick<TState, TNonFunctionKeys<TState>>;

const usePaymentStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        setter: fn => set(fn, false, "usePaymentStore/setter"),
        hasEssentialsPlan: () => useUserStore.getState().user.package_subscription?.name! === "ESSENTIALS",
        contactSales: (subject, companyName) => {
          window.open(`mailto:sales@refluenced.com?subject=${subject} - ${companyName}`, "_blank");
        },
        getCost: (formatter, plan, period) => {
          const selectedPeriod = period ?? get().selectedPeriod;

          return formatter(
            Math.round(
              selectedPeriod === "MONTHLY"
                ? plan?.price_per_month_with_discount!
                : plan?.price_per_year_with_discount! / YEAR_IN_MONTHS,
            ),
          );
        },
        getIsCurrentPlan: plan => {
          const currentPlanCategory = useUserStore.getState().user.package_subscription?.package_category;
          const currentPlanType = useUserStore.getState().user.package_subscription?.package_type;
          const currentPlanName = useUserStore.getState().user.package_subscription?.name;

          return (
            plan.name === currentPlanName &&
            get().selectedPeriod === currentPlanType &&
            currentPlanCategory === get().getPlanCategory()
          );
        },
        getPlanCategory: () => {
          if (get().isInstagramSelected && get().isTiktokSelected) {
            return "BOTH";
          } else if (get().isInstagramSelected) {
            return "INSTAGRAM";
          } else {
            return "TIKTOK";
          }
        },
        getPlans: c => {
          let category = c || get().getPlanCategory();

          if (category === "BOTH") {
            return get().both;
          } else if (category === "INSTAGRAM") {
            return get().instagram;
          } else {
            return get().tiktok;
          }
        },
      }),
      {name: "usePaymentStore"},
    ),
  ),
);

export {usePaymentStore};
